import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { fetchTutorProfile, updateTutorProfile } from '../../api/tutorProfile';
import TutorReviewsDialog from '../../components/admin/TutorReviewsDialog';
import ReviewSortControls from '../../components/reviews/ReviewSortControls';
import sortReviews from '../../components/reviews/sortReviews';

const profileFields = [
  { key: 'subject', label: 'Предмет' },
  { key: 'specialization', label: 'Спеціалізація' },
  { key: 'grades', label: 'Класи' },
  { key: 'education', label: 'Освіта', multiline: true },
  { key: 'experience', label: 'Досвід викладання', multiline: true },
  { key: 'bio', label: 'Біографія', multiline: true },
  { key: 'scientificInterests', label: 'Наукові інтереси', multiline: true },
  { key: 'workRules', label: 'Правила роботи', multiline: true },
];

const AdminTutorProfilePage = () => {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [reviewSort, setReviewSort] = useState('newest');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['tutor-profile', tutorId],
    queryFn: () => fetchTutorProfile(tutorId),
    enabled: Boolean(tutorId),
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => updateTutorProfile(tutorId, payload),
    onSuccess: () => {
      enqueueSnackbar('Профіль оновлено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutor-profile', tutorId] });
      setEditing(false);
    },
    onError: () => enqueueSnackbar('Не вдалося оновити профіль', { variant: 'error' }),
  });

  const handleEdit = () => {
    const data = {};
    profileFields.forEach((f) => { data[f.key] = profile?.[f.key] || ''; });
    setFormData(data);
    setEditing(true);
  };

  const handleChange = (key) => (e) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const completionRate = profile?.totalLessons > 0
    ? `${Math.round((profile.completedLessons / profile.totalLessons) * 100)}%`
    : '0%';

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return <Typography>Профіль не знайдено.</Typography>;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/admin/tutors')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>{profile.tutorName}</Typography>
            {profile.subject && (
              <Typography variant="body1" color="text.secondary">{profile.subject}</Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          {!editing && (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
              Редагувати
            </Button>
          )}
          <Button variant="outlined" onClick={() => setReviewsOpen(true)}>
            Відгуки
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            {profileFields.map((field) => (
              <Grid item xs={12} md={6} key={field.key}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {field.label}
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={formData[field.key] || ''}
                    onChange={handleChange(field.key)}
                    multiline={field.multiline}
                    rows={field.multiline ? 3 : 1}
                  />
                ) : (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {profile[field.key] || '---'}
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>

          {editing && (
            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => setEditing(false)}>
                Скасувати
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => updateMutation.mutate(formData)}
                disabled={updateMutation.isPending}
              >
                Зберегти
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>Статистика</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {profile.completedLessons ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Проведено уроків</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {profile.averageRating ? profile.averageRating.toFixed(1) : '—'}
                  </Typography>
                  <Rating value={profile.averageRating || 0} precision={0.1} readOnly />
                </Stack>
                <Typography variant="body2" color="text.secondary">Середній бал</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>Відгуки</Typography>
          {profile.reviews?.length > 1 && (
            <Box sx={{ mb: 2 }}>
              <ReviewSortControls value={reviewSort} onChange={setReviewSort} />
            </Box>
          )}
          {(!profile.reviews || profile.reviews.length === 0) ? (
            <Typography color="text.secondary">Відгуків поки немає.</Typography>
          ) : (
            <Stack spacing={2} divider={<Divider />}>
              {sortReviews(profile.reviews, reviewSort).map((review) => (
                <Box key={review.id}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600}>{review.studentName}</Typography>
                    <Rating value={review.rating} readOnly size="small" />
                  </Stack>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{review.text}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(review.createdAt).format('DD.MM.YYYY')}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <TutorReviewsDialog
        open={reviewsOpen}
        onClose={() => {
          setReviewsOpen(false);
          queryClient.invalidateQueries({ queryKey: ['tutor-profile', tutorId] });
        }}
        tutor={profile ? { id: Number(tutorId), name: profile.tutorName } : null}
      />
    </Box>
  );
};

export default AdminTutorProfilePage;
